<template>
  <div class="modal fade" id="poi-dialog" tabindex="-1" aria-labelledby="poiDialogLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
<h5 class="modal-title" id="poiDialogLabel">{{ dialogTitle }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('shared.close')"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <div class="mb-3">
              <label for="poi-type" class="form-label">{{ $t('shared.type_2') }}</label>
              <select v-model="poiData.type" class="form-select" id="poi-type" required>
                <option value="" disabled>{{ $t('plan_trail.poi_dialog.select_a_type') }}</option>
                <option v-for="(type, index) in poiTypes" :key="type.poi" :value="type.serverType()">
                  {{ type.prettyName() }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label for="poi-title" class="form-label">{{ $t('shared.title') }}</label>
              <input
                v-model="poiData.title"
                type="text"
                class="form-control"
                id="poi-title"
                :placeholder="$t('plan_trail.poi_dialog.enter_poi_name')"
                required
                maxlength="255"
              >
            </div>

            <div class="mb-3">
              <label for="poi-description" class="form-label">{{ $t('shared.description') }}</label>
              <textarea
                v-model="poiData.description"
                class="form-control"
                id="poi-description"
                rows="3"
                :placeholder="$t('plan_trail.poi_dialog.add_any_additional_details')"
                maxlength="1000"
              ></textarea>
            </div>

            <div class="mb-3" v-if="hasMultipleDays">
              <label for="poi-day" class="form-label">{{ $t('plan_trail.poi_dialog.day_for_multi_day_hikes') }}</label>
              <select v-model="poiData.dayNumber" class="form-select" id="poi-day">
                <option :value="undefined">{{ $t('plan_trail.poi_dialog.not_specific_to_a_day') }}</option>
                <option v-for="day in availableDays" :key="day" :value="day">
                  {{ $t('plan_trail.poi_dialog.day_day_name', { day: day, name: dayInfo[day]?.name ? ': ' + dayInfo[day].name : '' }) }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label for="poi-notification-distance" class="form-label">{{ $t('plan_trail.poi_dialog.nearby_notification') }}</label>
              <select v-model="poiData.notificationDistance" class="form-select" id="poi-notification-distance">
                <option :value="null">{{ $t('plan_trail.poi_dialog.disabled') }}</option>
                <option v-for="opt in notificationDistanceOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
              <small v-if="notifiableLimitReached" class="text-danger">
                {{ $t('plan_trail.poi_dialog.you_ve_reached_the_limit_of', { notifiable_highlights_per_hike_limit: NOTIFIABLE_HIGHLIGHTS_PER_HIKE_LIMIT }) }}
              </small>
            </div>

            <div class="mb-3" v-if="existingImages.length > 0">
              <label class="form-label">{{ $t('plan_trail.poi_dialog.existing_images') }}</label>
              <div class="d-flex flex-wrap gap-2 mt-2">
                <div
                  v-for="(image, index) in existingImages"
                  :key="image.clientId"
                  class="position-relative"
                >
                  <img
                    :src="image.url"
                    class="img-thumbnail"
                    style="width: 100px; height: 100px; object-fit: cover;"
                    :class="{ 'opacity-50': image.markedForRemoval }"
                  >
                  <button
                    type="button"
                    class="btn btn-sm btn-dark position-absolute top-0 end-0"
                    style="padding: 2px 6px; font-size: 12px;"
                    @click="toggleExistingImageRemoval(index)"
                  >
                    {{ image.markedForRemoval ? $t('plan_trail.poi_dialog.undo') : $t('shared.remove') }}
                  </button>
                </div>
              </div>
              <small v-if="hasExistingMarkedForRemoval" class="text-muted d-block mt-1">
                {{ $t('plan_trail.poi_dialog.images_marked_for_removal_will_be') }}
              </small>
            </div>

            <div class="mb-3" v-if="pendingUploadPreviews.length > 0">
              <label class="form-label">{{ $t('plan_trail.poi_dialog.pending_uploads') }}</label>
              <div class="d-flex flex-wrap gap-2 mt-2">
                <div
                  v-for="pending in pendingUploadPreviews"
                  :key="pending.clientId"
                  class="position-relative"
                >
                  <img
                    :src="pending.url"
                    class="img-thumbnail"
                    style="width: 100px; height: 100px; object-fit: cover;"
                  >
                  <button
                    type="button"
                    class="btn btn-sm btn-danger position-absolute top-0 end-0"
                    style="padding: 2px 6px; font-size: 12px;"
                    @click="removePendingUpload(pending.clientId)"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label for="poi-images" class="form-label">{{ $t('plan_trail.poi_dialog.images_optional') }}</label>
              <input
                type="file"
                class="form-control"
                id="poi-images"
                accept="image/*"
                multiple
                @change="handleImageSelect"
                ref="imageInput"
              >
              <div v-if="selectedImages.length > 0" class="mt-2">
                <small class="text-muted">{{ $t('plan_trail.poi_dialog.selected_images_image_s_selected', { selected_images: selectedImages.length }) }}</small>
                <div class="d-flex flex-wrap gap-2 mt-2">
                  <div v-for="(image, index) in imagePreview" :key="index" class="position-relative">
                    <img :src="image" class="img-thumbnail" style="width: 100px; height: 100px; object-fit: cover;">
                    <button
                      type="button"
                      class="btn btn-sm btn-danger position-absolute top-0 end-0"
                      style="padding: 2px 6px; font-size: 12px;"
                      @click="removeImage(index)"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="mb-3" v-if="existingMedia.length > 0">
              <label class="form-label">{{ $t('plan_trail.poi_dialog.existing_audio') }}</label>
              <div class="d-flex flex-column gap-2 mt-2">
                <div
                  v-for="(item, index) in existingMedia"
                  :key="item.clientId"
                  class="d-flex align-items-center gap-2"
                >
                  <audio :src="item.url" controls preload="none" class="flex-grow-1"></audio>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="removeExistingMedia(index)"
                  >
                    {{ $t('shared.remove') }}
                  </button>
                </div>
              </div>
            </div>

            <div class="mb-3" v-if="pendingMedia.length > 0">
              <label class="form-label">{{ $t('plan_trail.poi_dialog.new_audio') }}</label>
              <div class="d-flex flex-column gap-2 mt-2">
                <div
                  v-for="item in pendingMedia"
                  :key="item.clientId"
                  class="d-flex align-items-center gap-2"
                >
                  <audio :src="item.previewUrl" controls preload="none" class="flex-grow-1"></audio>
                  <button
                    type="button"
                    class="btn btn-sm btn-danger"
                    @click="removePendingMedia(item.clientId)"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            <div class="mb-3" v-if="!hasAudio || isRecording">
              <label for="poi-audio" class="form-label">{{ $t('plan_trail.poi_dialog.audio_optional') }}</label>
              <div class="d-flex gap-2 align-items-center">
                <input
                  type="file"
                  class="form-control"
                  id="poi-audio"
                  accept="audio/*,.mp3,.m4a,.mp4,.aac,.wav,.ogg,.webm"
                  @change="handleAudioSelect"
                  ref="audioInput"
                >
                <button
                  v-if="!isRecording"
                  type="button"
                  class="btn btn-outline-secondary flex-shrink-0"
                  @click="startRecording"
                  :disabled="!canRecord"
                  :title="$t('plan_trail.poi_dialog.record_audio')"
                >
                  <i class="bi bi-mic"></i> {{ $t('plan_trail.poi_dialog.record') }}
                </button>
                <button
                  v-else
                  type="button"
                  class="btn btn-danger flex-shrink-0"
                  @click="stopRecording"
                >
                  <i class="bi bi-stop-fill"></i> {{ $t('plan_trail.poi_dialog.stop_recording_seconds_s', { recording_seconds: recordingSeconds }) }}
                </button>
              </div>
              <small v-if="recordingError" class="text-danger d-block mt-1">{{ recordingError }}</small>
              <small v-else class="text-muted d-block mt-1">
                {{ $t('plan_trail.poi_dialog.add_an_audio_guide_for_this') }}
              </small>
            </div>

            <div class="alert alert-info" role="alert">
              <small>
                <i class="bi bi-info-circle"></i>
                {{ $t('plan_trail.poi_dialog.location_coordinates', { coordinates: formatCoordinates(coordinates) }) }}
              </small>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('shared.cancel_2') }}</button>
          <button type="button" class="btn btn-primary" @click="handleSubmit" :disabled="!isValid || uploadingImages">
            <span v-if="uploadingImages">
              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ $t('plan_trail.poi_dialog.uploading') }}
            </span>
            <span v-else>{{ submitLabel }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType, watch } from 'vue';
import { useStore } from 'vuex';
import {
  NOTIFIABLE_HIGHLIGHTS_PER_HIKE_LIMIT,
  countOtherNotifiableHighlights,
  isNotifiableLimitReached as computeIsNotifiableLimitReached,
} from '../constants/notifiableHighlightLimit';
import { Modal } from 'bootstrap';
import {POIUtility} from "../../../utils/models/POI";
import { MeasurementPrefUtility } from "../../../utils/helpers/MeasurementPref";
import graphClient from "../../../utils/graphql/graph_client";
import { GenerateDirectUpload } from "../../../utils/graphql/planned-trail-graph";
import { resolveAudioContentType } from "../../../utils/resolveAudioContentType";
import { i18n } from "../../../i18n";
import SparkMD5 from 'spark-md5';

type HighlightImageUpload = {
  signedId: string;
  clientId: string;
};

type PendingUploadPreview = {
  clientId: string;
  url: string;
};

type ExistingHighlightImage = {
  id?: string | number;
  clientId: string;
  url: string;
  markedForRemoval: boolean;
  raw: any;
};

type HikeHighlight = {
  clientId: string;
  type: string;
  title: string;
  description?: string;
  coordinates: [number, number];
  dayNumber?: number;
  notificationDistance?: number | null;
  imageSignedIds?: Array<HighlightImageUpload | string>;
  images?: Array<{
    id?: string | number;
    clientId?: string;
    file?: {
      lgUrl?: string;
      smUrl?: string;
      mdUrl?: string;
      url?: string;
    };
    _destroy?: boolean;
  }>;
  imagePreviewUrls?: string[];
  mediaSignedIds?: Array<{
    signedId: string;
    clientId: string;
    mediaType: string;
    title?: string;
    previewUrl?: string;
  }>;
  media?: Array<{
    id?: string | number;
    clientId: string;
    mediaType: string;
    contentType?: string;
    title?: string;
    description?: string;
    url?: string;
    _destroy?: boolean;
  }>;
};

type ExistingHighlightMedia = {
  id?: string | number;
  clientId: string;
  mediaType: string;
  contentType?: string;
  title?: string;
  description?: string;
  url?: string;
};

type PendingMedia = {
  clientId: string;
  signedId?: string;
  file?: Blob;
  name: string;
  previewUrl: string;
  title?: string;
  mediaType: string;
};

const generateHighlightImageClientId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `highlight_image_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

export default defineComponent({
  name: 'POIDialog',
  props: {
    coordinates: {
      type: Array as PropType<[number, number]>,
      required: true
    },
    existingHighlight: {
      type: Object as PropType<HikeHighlight | null>,
      default: null
    }
  },
  setup(props) {
    const store = useStore();
    
    // Get POI types from POIUtility
    const poiTypes = POIUtility.allPOIUtils();

    const notificationDistanceOptions = computed(() => {
      const pref = MeasurementPrefUtility.preference();
      const bigUnit = pref.kilometerSymbol();
      const smallUnit = pref.meterSymbol();
      return [
        { value: 1,   label: i18n.global.t('plan_trail.poi_dialog.within_distance', { distance: `1${bigUnit}` }) },
        { value: 0.5, label: i18n.global.t('plan_trail.poi_dialog.within_distance', { distance: `500${smallUnit}` }) },
        { value: 0.2, label: i18n.global.t('plan_trail.poi_dialog.within_distance', { distance: `200${smallUnit}` }) },
        { value: 0.1, label: i18n.global.t('plan_trail.poi_dialog.within_distance', { distance: `100${smallUnit}` }) }
      ];
    });

    const poiData = ref({
      type: '',
      title: '',
      description: '',
      dayNumber: undefined as number | undefined,
      notificationDistance: null as number | null
    });

    const selectedImages = ref<File[]>([]);
    const imagePreview = ref<string[]>([]);
    const imageInput = ref<HTMLInputElement | null>(null);
    const uploadingImages = ref(false);
    const existingImages = ref<ExistingHighlightImage[]>([]);
    const pendingUploads = ref<HighlightImageUpload[]>([]);
    const pendingUploadPreviews = ref<PendingUploadPreview[]>([]);

    // Audio media (parallel track to images)
    const existingMedia = ref<ExistingHighlightMedia[]>([]);
    const pendingMedia = ref<PendingMedia[]>([]);
    const audioInput = ref<HTMLInputElement | null>(null);
    const isRecording = ref(false);
    const recordingError = ref('');
    const recordingSeconds = ref(0);
    let mediaRecorder: MediaRecorder | null = null;
    let recordedChunks: Blob[] = [];
    let recordingTimer: number | null = null;
    const canRecord = computed(() =>
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      typeof (window as any).MediaRecorder !== 'undefined'
    );
    // One audio clip per highlight: existing (kept) + newly-added pending.
    const hasAudio = computed(() => existingMedia.value.length + pendingMedia.value.length > 0);

    const isEditMode = computed(() => Boolean(props.existingHighlight?.clientId));

    const normaliseUploads = (uploads?: Array<HighlightImageUpload | string>) => {
      if (!uploads || uploads.length === 0) {
        return [] as HighlightImageUpload[];
      }

      return uploads
        .map((entry) => {
          if (!entry) return null;

          if (typeof entry === 'string') {
            return {
              signedId: entry,
              clientId: generateHighlightImageClientId()
            };
          }

          const signedId = entry.signedId;
          if (!signedId) {
            return null;
          }

          return {
            signedId,
            clientId: entry.clientId || generateHighlightImageClientId()
          };
        })
        .filter(Boolean) as HighlightImageUpload[];
    };

    const initialiseForm = () => {
      if (isEditMode.value && props.existingHighlight) {
        poiData.value = {
          type: props.existingHighlight.type || '',
          title: props.existingHighlight.title || '',
          description: props.existingHighlight.description || '',
          dayNumber: props.existingHighlight.dayNumber,
          notificationDistance: props.existingHighlight.notificationDistance ?? null
        };
      } else {
        poiData.value = {
          type: '',
          title: '',
          description: '',
          dayNumber: undefined,
          notificationDistance: null
        };
      }

      selectedImages.value = [];
      imagePreview.value = [];
      if (imageInput.value) {
        imageInput.value.value = '';
      }

      if (props.existingHighlight) {
        const highlightImages = props.existingHighlight.images || [];
        existingImages.value = highlightImages
          .map((image: any) => {
            if (!image) return null;
            const clientId = image.clientId || image.client_id || generateHighlightImageClientId();
            const url = image.file?.lgUrl || image.file?.mdUrl || image.file?.url || image.url || '';
            if (!url) {
              return null;
            }

            return {
              id: image.id,
              clientId,
              url,
              markedForRemoval: Boolean(image._destroy),
              raw: {
                ...image,
                clientId
              }
            } as ExistingHighlightImage;
          })
          .filter(Boolean) as ExistingHighlightImage[];

        const normalisedExistingUploads = normaliseUploads(props.existingHighlight.imageSignedIds);
        pendingUploads.value = [...normalisedExistingUploads];

        const previewUrls = props.existingHighlight.imagePreviewUrls || [];
        pendingUploadPreviews.value = normalisedExistingUploads
          .map((upload, index) => ({
            clientId: upload.clientId,
            url: previewUrls[index] || ''
          }))
          .filter((entry) => entry.url);

        // Audio: persisted media (have a url) + media already uploaded this
        // session but not yet saved (have a signedId + previewUrl).
        existingMedia.value = (props.existingHighlight.media || [])
          .filter((item: any) => item && !item._destroy)
          .map((item: any) => ({
            id: item.id,
            clientId: item.clientId || generateHighlightImageClientId(),
            mediaType: item.mediaType || 'audio',
            contentType: item.contentType,
            title: item.title,
            description: item.description,
            url: item.url
          }));

        pendingMedia.value = (props.existingHighlight.mediaSignedIds || [])
          .filter((upload: any) => upload && upload.signedId)
          .map((upload: any) => ({
            clientId: upload.clientId || generateHighlightImageClientId(),
            signedId: upload.signedId,
            name: upload.title || i18n.global.t('plan_trail.poi_dialog.audio'),
            previewUrl: upload.previewUrl || '',
            title: upload.title,
            mediaType: upload.mediaType || 'audio'
          }));
      } else {
        existingImages.value = [];
        pendingUploads.value = [];
        pendingUploadPreviews.value = [];
        existingMedia.value = [];
        pendingMedia.value = [];
      }
    };

    watch(() => props.existingHighlight, () => {
      initialiseForm();
    }, { immediate: true });

    const dayInfo = computed(() => store.state.planTrail.dayInfo || {});
    const routes = computed(() => store.state.planTrail.routes || {});

    const hasMultipleDays = computed(() => {
      const routeValues = Object.values(routes.value);
      const uniqueDays = new Set(routeValues.map((r: any) => r.day).filter(d => d != null));
      return uniqueDays.size > 1;
    });

    const availableDays = computed(() => {
      const routeValues = Object.values(routes.value);
      const days = new Set(routeValues.map((r: any) => r.day).filter(d => d != null));
      return Array.from(days).sort((a, b) => a - b);
    });

    const dialogTitle = computed(() => isEditMode.value ? i18n.global.t('plan_trail.poi_dialog.edit_hike_highlight') : i18n.global.t('plan_trail.poi_dialog.add_hike_highlight'));
    const submitLabel = computed(() => isEditMode.value ? i18n.global.t('plan_trail.poi_dialog.save_changes') : i18n.global.t('shared.save'));

    const notifiableHighlightsCount = computed(() =>
      countOtherNotifiableHighlights(
        store.state.planTrail.hikeHighlights || [],
        props.existingHighlight?.clientId,
      )
    );

    const notifiableLimitReached = computed(() =>
      computeIsNotifiableLimitReached(
        poiData.value.notificationDistance != null,
        store.state.planTrail.hikeHighlights || [],
        props.existingHighlight?.clientId,
      )
    );

    const isValid = computed(() => {
      return poiData.value.type && poiData.value.title.trim().length > 0 && !notifiableLimitReached.value;
    });

    const formatCoordinates = (coords?: [number, number]) => {
      if (!coords) return '—';
      return `${coords[1].toFixed(6)}, ${coords[0].toFixed(6)}`;
    };

    const calculateMD5Base64 = async (buffer: ArrayBuffer): Promise<string> => {
      const spark = new SparkMD5.ArrayBuffer();
      spark.append(buffer);
      const hash = spark.end();
      const bytes = new Uint8Array(hash.match(/.{2}/g).map(byte => parseInt(byte, 16)));
      return btoa(String.fromCharCode(...bytes));
    };

    const handleImageSelect = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const files = target.files;
      if (!files) return;

      // Clear previous selections
      selectedImages.value = [];
      imagePreview.value = [];

      // Process new files
      Array.from(files).forEach(file => {
        selectedImages.value.push(file);
        
        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            imagePreview.value.push(e.target.result as string);
          }
        };
        reader.readAsDataURL(file);
      });
    };

    const removeImage = (index: number) => {
      selectedImages.value.splice(index, 1);
      imagePreview.value.splice(index, 1);
      
      // Clear the input if no images left
      if (selectedImages.value.length === 0 && imageInput.value) {
        imageInput.value.value = '';
      }
    };

    const toggleExistingImageRemoval = (index: number) => {
      const image = existingImages.value[index];
      if (!image) return;
      existingImages.value.splice(index, 1, {
        ...image,
        markedForRemoval: !image.markedForRemoval
      });
    };

    const removePendingUpload = (clientId: string) => {
      pendingUploads.value = pendingUploads.value.filter(upload => upload.clientId !== clientId);
      pendingUploadPreviews.value = pendingUploadPreviews.value.filter(preview => preview.clientId !== clientId);
    };

    const hasExistingMarkedForRemoval = computed(() => existingImages.value.some(image => image.markedForRemoval));

    const uploadImages = async (): Promise<HighlightImageUpload[]> => {
      const uploads: HighlightImageUpload[] = [];
      
      for (const file of selectedImages.value) {
        try {
          // Calculate MD5 checksum
          const arrayBuffer = await file.arrayBuffer();
          const hashBase64 = await calculateMD5Base64(arrayBuffer);

          // Step 1: Generate direct upload credentials
          const response = await graphClient.request(GenerateDirectUpload, {
            input: {
              filename: file.name,
              byteSize: file.size,
              contentType: file.type || 'image/jpeg',
              checksumBase64: hashBase64,
              serviceName: window.publicBucketService
            }
          });

          const { uploadUrl, headers, signedId } = response.generateDirectUpload;

          // Step 2: Upload file directly to storage
          const uploadHeaders = JSON.parse(headers);
          const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
              ...uploadHeaders,
              'Content-Type': file.type || 'image/jpeg'
            },
            body: file
          });

          if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text();
            console.error('Upload failed:', uploadResponse.status, errorText);
            throw new Error(`Upload failed: ${uploadResponse.statusText}`);
          }

          uploads.push({
            signedId,
            clientId: generateHighlightImageClientId()
          });
        } catch (error) {
          console.error('Error uploading image:', error);
          throw error;
        }
      }

      return uploads;
    };

    const handleAudioSelect = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const files = target.files;
      if (!files) return;

      // One audio clip per highlight: ignore the pick if one already exists,
      // and only ever take the first selected file.
      const file = files[0];
      if (file && !hasAudio.value) {
        pendingMedia.value.push({
          clientId: generateHighlightImageClientId(),
          file,
          name: file.name,
          previewUrl: URL.createObjectURL(file),
          title: file.name,
          mediaType: 'audio'
        });
      }

      if (audioInput.value) {
        audioInput.value.value = '';
      }
    };

    const startRecording = async () => {
      recordingError.value = '';
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        recordedChunks = [];
        const recorder = new MediaRecorder(stream);
        mediaRecorder = recorder;

        recorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            recordedChunks.push(e.data);
          }
        };

        recorder.onstop = () => {
          const type = recorder.mimeType || 'audio/webm';
          const blob = new Blob(recordedChunks, { type });
          // One audio clip per highlight: only keep the recording if none exists.
          if (!hasAudio.value) {
            pendingMedia.value.push({
              clientId: generateHighlightImageClientId(),
              file: blob,
              name: i18n.global.t('shared.recording'),
              previewUrl: URL.createObjectURL(blob),
              title: i18n.global.t('shared.recording'),
              mediaType: 'audio'
            });
          }
          stream.getTracks().forEach((track) => track.stop());
        };

        recorder.start();
        isRecording.value = true;
        recordingSeconds.value = 0;
        recordingTimer = window.setInterval(() => {
          recordingSeconds.value += 1;
        }, 1000);
      } catch (error) {
        console.error('Recording failed:', error);
        recordingError.value = i18n.global.t('plan_trail.poi_dialog.microphone_denied');
      }
    };

    const stopRecording = () => {
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
      isRecording.value = false;
      if (recordingTimer) {
        clearInterval(recordingTimer);
        recordingTimer = null;
      }
    };

    const removePendingMedia = (clientId: string) => {
      const item = pendingMedia.value.find((m) => m.clientId === clientId);
      // Only revoke object URLs we created locally (blobs), not server preview URLs.
      if (item?.file && item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
      pendingMedia.value = pendingMedia.value.filter((m) => m.clientId !== clientId);
    };

    const removeExistingMedia = (index: number) => {
      existingMedia.value.splice(index, 1);
    };

    // Upload any pending audio blobs that don't yet have a signedId. Reuses the
    // same generateDirectUpload flow as images; the backend caps audio larger and
    // validates the MIME against the audio allowlist on attach.
    const uploadPendingMedia = async (): Promise<void> => {
      for (const item of pendingMedia.value) {
        if (item.signedId || !item.file) continue;

        const arrayBuffer = await item.file.arrayBuffer();
        const hashBase64 = await calculateMD5Base64(arrayBuffer);
        // Normalise to an allowed audio MIME. The browser reports an .mp4 picked
        // as audio with `video/mp4` (or no type), which the server's audio
        // allowlist rejects — silently dropping the upload. Resolve by extension
        // so the blob is presigned as audio and actually persists.
        const filename = item.name || `audio_${Date.now()}`;
        const contentType = resolveAudioContentType(filename, item.file.type);

        const response = await graphClient.request(GenerateDirectUpload, {
          input: {
            filename,
            byteSize: item.file.size,
            contentType,
            checksumBase64: hashBase64,
            serviceName: window.publicBucketService
          }
        });

        const { uploadUrl, headers, signedId } = response.generateDirectUpload;
        const uploadHeaders = JSON.parse(headers);
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            ...uploadHeaders,
            'Content-Type': contentType
          },
          body: item.file
        });

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text();
          console.error('Audio upload failed:', uploadResponse.status, errorText);
          throw new Error(`Audio upload failed: ${uploadResponse.statusText}`);
        }

        item.signedId = signedId;
      }
    };

    const buildMediaUploadsPayload = () =>
      pendingMedia.value
        .filter((item) => item.signedId)
        .map((item) => ({
          signedId: item.signedId as string,
          clientId: item.clientId,
          mediaType: item.mediaType,
          title: item.title,
          previewUrl: item.previewUrl
        }));

    const handleSubmit = async () => {
      if (!isValid.value) return;

      uploadingImages.value = true;

      // Flush any in-progress recording so its blob is captured before upload.
      if (isRecording.value) {
        stopRecording();
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      try {
        const trimmedTitle = poiData.value.title.trim();
        const trimmedDescription = poiData.value.description.trim();

        // Upload images if any
        let uploadedImageUploads: HighlightImageUpload[] = [];
        if (selectedImages.value.length > 0) {
          uploadedImageUploads = await uploadImages();
        }

        // Upload any new audio recordings/files
        await uploadPendingMedia();
        const mediaUploads = buildMediaUploadsPayload();

        if (isEditMode.value && props.existingHighlight) {
          const updates: Partial<HikeHighlight> = {
            type: poiData.value.type,
            title: trimmedTitle,
            description: trimmedDescription,
            coordinates: props.coordinates,
            dayNumber: poiData.value.dayNumber,
            notificationDistance: poiData.value.notificationDistance
          };

          const combinedExistingUploads = [...pendingUploads.value];
          const combinedExistingPreviews = [...pendingUploadPreviews.value];

          if (uploadedImageUploads.length > 0) {
            uploadedImageUploads.forEach((upload, index) => {
              combinedExistingUploads.push(upload);
              combinedExistingPreviews.push({
                clientId: upload.clientId,
                url: imagePreview.value[index] || ''
              });
            });
          }

          updates.imageSignedIds = combinedExistingUploads;
          updates.imagePreviewUrls = combinedExistingPreviews.map(entry => entry.url);
          updates.images = existingImages.value.map(image => ({
            ...image.raw,
            clientId: image.clientId,
            _destroy: image.markedForRemoval || undefined
          }));

          updates.media = existingMedia.value.map(item => ({ ...item }));
          updates.mediaSignedIds = mediaUploads;

          store.commit('planTrail/updateHikeHighlight', {
            clientId: props.existingHighlight.clientId,
            updates
          });

          pendingUploads.value = combinedExistingUploads;
          pendingUploadPreviews.value = combinedExistingPreviews;
        } else {
          const newPreviewEntries = uploadedImageUploads.map((upload, index) => imagePreview.value[index] || '');
          store.commit('planTrail/addHikeHighlight', {
            type: poiData.value.type,
            title: trimmedTitle,
            description: trimmedDescription,
            coordinates: props.coordinates,
            dayNumber: poiData.value.dayNumber,
            notificationDistance: poiData.value.notificationDistance,
            imageSignedIds: uploadedImageUploads.length > 0 ? uploadedImageUploads : undefined,
            imagePreviewUrls: newPreviewEntries.length > 0 ? newPreviewEntries : undefined,
            images: [],
            mediaSignedIds: mediaUploads.length > 0 ? mediaUploads : undefined,
            media: []
          });
        }

        await store.dispatch('planTrail/refreshHighlights');

        if (!isEditMode.value) {
          initialiseForm();
        } else {
          selectedImages.value = [];
          imagePreview.value = [];
          if (imageInput.value) {
            imageInput.value.value = '';
          }
        }

        const modalElement = document.getElementById('poi-dialog');
        if (modalElement) {
          const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
          modalInstance?.hide();
        }
      } catch (error) {
        console.error('Error submitting POI:', error);
        alert(i18n.global.t('plan_trail.poi_dialog.failed_to_upload_images'));
      } finally {
        uploadingImages.value = false;
      }
    };

    return {
      poiTypes,
      poiData,
      notificationDistanceOptions,
      dayInfo,
      hasMultipleDays,
      availableDays,
      dialogTitle,
      submitLabel,
      notifiableLimitReached,
      NOTIFIABLE_HIGHLIGHTS_PER_HIKE_LIMIT,
      isValid,
      formatCoordinates,
      handleSubmit,
      selectedImages,
      imagePreview,
      imageInput,
      uploadingImages,
      handleImageSelect,
      removeImage,
      existingImages,
      pendingUploadPreviews,
      toggleExistingImageRemoval,
      removePendingUpload,
      hasExistingMarkedForRemoval,
      existingMedia,
      pendingMedia,
      audioInput,
      isRecording,
      recordingError,
      recordingSeconds,
      canRecord,
      hasAudio,
      handleAudioSelect,
      startRecording,
      stopRecording,
      removePendingMedia,
      removeExistingMedia
    };
  }
});
</script>
